import CssBaseline from '@mui/material/CssBaseline';
import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { appScrollbarThumbColor, appScrollbarThumbHoverColor, appScrollbarTrackColor } from './lib/scrollbarSx';
import Home from './pages/home/home';
import Sheet from './pages/sheet/sheet';
import Documents from './pages/documents/documents';
import Admin from './pages/Admin/admin';
import AdminUsers from './pages/AdminUsers/AdminUsers';
import AdminDiscounts from './pages/AdminDiscounts/AdminDiscounts';
import Auth from './components/Auth';
import UnderConstruction from './pages/UnderConstruction';
import MessageProvider from './components/MessageProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminAccessProvider, useAdminAccess } from './contexts/AdminAccessContext';
import { UserSettingsProvider, useUserSettings } from './contexts/UserSettingsContext';
import { hasAnonymousSession } from './lib/anonymousSession';
import { trackPageView } from './lib/analytics';
import TermsOfUsePage from './pages/legal/TermsOfUsePage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import PromptCommands from './pages/PromptCommands';
import PromptHelp from './pages/PromptHelp';
import PlansPage from './pages/plans/plans';

// Check for whitelist cookie
function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

const isWhitelisted = () => {
  return getCookie('whitelist') === 'playmachine';
};

function toBoolean(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'off'].includes(normalized)) {
      return false;
    }
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  return defaultValue;
}

async function fetchRequireWhitelistCookie(): Promise<boolean> {
  try {
    const response = await fetch('/api/config/flags', {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      return true;
    }

    const payload = await response.json();
    return toBoolean(payload?.flags?.require_whitelist_cookie, true);
  } catch {
    return true;
  }
}

function RouteAnalyticsTracker({ hasSession, isAuthenticated }: { hasSession: boolean; isAuthenticated: boolean }) {
  const location = useLocation();

  React.useEffect(() => {
    let pageName = 'unknown';

    if (location.pathname === '/') {
      pageName = 'home';
    } else if (location.pathname === '/documents') {
      pageName = 'documents';
    } else if (location.pathname === '/terms') {
      pageName = 'terms';
    } else if (location.pathname === '/privacy') {
      pageName = 'privacy';
    } else if (location.pathname === '/plans') {
      pageName = 'plans';
    } else if (location.pathname === '/prompt-commands') {
      pageName = 'prompt_commands';
    } else if (location.pathname === '/prompt-help') {
      pageName = 'prompt_help';
    } else if (location.pathname === '/admin') {
      pageName = 'admin';
    } else if (location.pathname === '/admin/users') {
      pageName = 'admin_users';
    } else if (location.pathname === '/admin/discounts') {
      pageName = 'admin_discounts';
    } else if (location.pathname.startsWith('/sheet/')) {
      pageName = 'sheet';
    }

    if (pageName !== 'unknown') {
      trackPageView(location.pathname, pageName);
    }
  }, [hasSession, isAuthenticated, location.pathname]);

  return null;
}

function buildTheme(mode: 'light' | 'dark') {
  const baseTheme = createTheme({
    palette: {
      mode,
      ...(mode === 'dark'
        ? {
          background: {
            default: '#0f1418',
            paper: '#161d22',
          },
        }
        : {}),
    },
    typography: {
      fontFamily: [
        '"Source Sans 3"',
        'Segoe UI',
        'Inter',
        'Roboto',
        'Arial',
        'Helvetica Neue',
        'Helvetica',
        'sans-serif',
      ].join(','),
    },
  });

  const scrollbarThumbColor = appScrollbarThumbColor(baseTheme);
  const scrollbarTrackColor = appScrollbarTrackColor(baseTheme);
  const scrollbarThumbHoverColor = appScrollbarThumbHoverColor(baseTheme);

  return createTheme(baseTheme, {
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          @font-face {
            font-family: 'Source Sans 3';
            font-style: normal;
            font-display: swap;
            font-weight: 400;
            src: url('/fonts/Source_Sans_3/static/SourceSans3-Regular.ttf') format('truetype');
          }

          * {
            scrollbar-width: thin;
            scrollbar-color: ${scrollbarThumbColor} ${scrollbarTrackColor};
          }

          *::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }

          *::-webkit-scrollbar-track {
            background-color: ${scrollbarTrackColor};
            border-radius: 999px;
          }

          *::-webkit-scrollbar-thumb {
            background-color: ${scrollbarThumbColor};
            border-radius: 999px;
            border: 2px solid ${scrollbarTrackColor};
          }

          *::-webkit-scrollbar-thumb:hover {
            background-color: ${scrollbarThumbHoverColor};
          }
        `,
      },
    },
  });
}

function ThemeShell() {
  const { settings } = useUserSettings();
  const theme = React.useMemo(
    () => buildTheme(settings.ui.themeMode),
    [settings.ui.themeMode],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppRoutes />
    </ThemeProvider>
  );
}

function AuthThemeShell() {
  const authTheme = React.useMemo(() => buildTheme('light'), []);

  return (
    <ThemeProvider theme={authTheme}>
      <CssBaseline />
      <Auth />
    </ThemeProvider>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const hasSession = hasAnonymousSession();

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RouteAnalyticsTracker hasSession={hasSession} isAuthenticated={Boolean(user)} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/documents" element={(user || hasSession) ? <Documents /> : <Home />} />
        <Route path="/sheet/:id" element={<Sheet />} />
        <Route path="/auth" element={<AuthThemeShell />} />
        <Route path="/terms" element={<TermsOfUsePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/prompt-commands" element={<PromptCommands />} />
        <Route path="/prompt-help" element={<PromptHelp />} />
        <Route path="/admin" element={<RequireAdminRoute><Admin /></RequireAdminRoute>} />
        <Route path="/admin/users" element={<RequireAdminRoute><AdminUsers /></RequireAdminRoute>} />
        <Route path="/admin/discounts" element={<RequireAdminRoute><AdminDiscounts /></RequireAdminRoute>} />
      </Routes>
    </Router>
  );
}

function RequireAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { isAdmin, isLoading, refresh } = useAdminAccess();
  const userId = user?.id;

  React.useEffect(() => {
    if (!loading && userId) {
      void refresh();
    }
  }, [loading, refresh, userId]);

  if (loading || isLoading) {
    return null;
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  const defaultTheme = React.useMemo(() => buildTheme('light'), []);
  const [flagsLoaded, setFlagsLoaded] = React.useState(false);
  const [requireWhitelistCookie, setRequireWhitelistCookie] = React.useState(true);

  React.useEffect(() => {
    let isMounted = true;

    fetchRequireWhitelistCookie()
      .then((required) => {
        if (isMounted) {
          setRequireWhitelistCookie(required);
        }
      })
      .finally(() => {
        if (isMounted) {
          setFlagsLoaded(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!flagsLoaded) {
    return (
      <ThemeProvider theme={defaultTheme}>
        <CssBaseline />
      </ThemeProvider>
    );
  }

  // Check whitelist cookie first
  if (requireWhitelistCookie && !isWhitelisted()) {
    return (
      <ThemeProvider theme={defaultTheme}>
        <CssBaseline />
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <UnderConstruction />
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <AuthProvider>
      <MessageProvider>
        <UserSettingsProvider>
          <AdminAccessProvider>
            <ThemeShell />
          </AdminAccessProvider>
        </UserSettingsProvider>
      </MessageProvider>
    </AuthProvider>
  );
}

export default App;
